import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { SearchInput } from "../../../../Forms/Inputs/SearchInput/React";
import {
  GeoLocation,
  getCurrentPosition,
  updateCurrentPositionPin
} from "../../../../Mapbox/mapbox";
import { Button, ButtonColor, ButtonSize } from "../../../../Shared/Buttons/React";
import { ErrorMessage, InlineError } from "../../components/shared/errorMessage";
import LoadingStatus from "../../components/shared/loadingStatus";
import SelectionItem from "../../components/shared/selectionItem";
import { Spinner } from "../../components/shared/spinner";
import { useProvetData, useProvetStatic, useTranslationContext } from "../../context";
import { useFocusAfterReload } from "../../hooks/useFocusAfterReload";
import { ProvetDepartment } from "../../types";
import {
  filterDepartmentsBySearch,
  getOldBookingUrl
} from "../../utilities/departmentUtilities";
import { LOADING_CONFIG } from "../../utilities/provetConstants";
import { OldBookingListItem } from "./oldBookingListItem";
import { DepartmentMap } from "./provetDepartmentMap";

interface ProvetDepartmentFinderProps {
  id?: string;
  activeProvetDepartmentIds: number[];
  excludedProvetDepartmentIds: number[];
  defaultLatitude: number;
  defaultLongitude: number;
  defaultZoom: number;
  oldBookingDomain?: string;
  mapboxAccessToken?: string;
  onSelectDepartment: (department: ProvetDepartment) => void;
}

/**
 * Department finder component for selecting a veterinary clinic location.
 *
 * Features:
 * - Search clinics by name, city, street address, or zip code
 * - Interactive map view with department markers
 * - Location services integration to find nearest clinics
 * - Clinics on the exclusion list are left out (by ProvetDataProvider)
 * - Clinics not on the "Active Provet departments" list link to the old booking
 * - Responsive list and map toggle view
 * - Error with "Try again" when the clinics fail to load, and a message when there are
 *   no clinics or a search matches nothing
 *
 * @param defaultLatitude - Initial map center latitude coordinate
 * @param defaultLongitude - Initial map center longitude coordinate
 * @param defaultZoom - Initial map zoom level (default: 4)
 * @param oldBookingDomain - Legacy booking domain URL for fallback links
 * @param mapboxAccessToken - Mapbox access token for map rendering
 * @param onSelectDepartment - Callback invoked when user selects a department
 *
 * @example
 * ```tsx
 * <ProvetDepartmentFinder
 *   excludedProvetDepartmentIds={[1, 2, 3]}
 *   defaultLatitude={59.3293}
 *   defaultLongitude={18.0686}
 *   defaultZoom={8}
 *   mapboxAccessToken="your-access-token"
 *   onSelectDepartment={(dept) => handleDepartmentSelection(dept)}
 * />
 * ```
 */
export const ProvetDepartmentFinder = ({
  defaultLatitude,
  defaultLongitude,
  defaultZoom = 4,
  oldBookingDomain,
  mapboxAccessToken,
  onSelectDepartment
}: ProvetDepartmentFinderProps) => {
  const mapId = "provet-department-finder-map";
  const [showMap, setShowMap] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [locationUnavailable, setLocationUnavailable] = useState(false);

  // The departments are fetched once by ProvetDataProvider; this component only reads them
  const { departments, departmentsStatus, reloadDepartments } = useProvetData();
  const { language } = useProvetStatic();
  const translationContext = useTranslationContext();
  const translations = translationContext?.translations;

  const { targetRef: resultsRef, retry: retryDepartments } = useFocusAfterReload(
    departmentsStatus,
    reloadDepartments
  );

  const loadError = useMemo(
    () => new Error(translations?.errorLoadingClinics ?? ""),
    [translations?.errorLoadingClinics]
  );

  // Filter departments based on search term - this IS worth memoizing (expensive filtering)
  const filteredDepartments = useMemo(
    () => filterDepartmentsBySearch(departments, searchTerm),
    [departments, searchTerm]
  );

  // Always rendered in a role="status" region, so changes to it are announced
  let listMessage = "";
  if (departmentsStatus === "loaded" && departments.length === 0) {
    listMessage = translations?.noClinicsAvailable ?? "";
  } else if (
    departmentsStatus === "loaded" &&
    !!searchTerm.trim() &&
    filteredDepartments.length === 0
  ) {
    listMessage = translations?.noClinicsFound ?? "";
  }

  const handleSearch = (value?: string) => {
    setSearchTerm(value || "");
  };

  const handleInput = (value?: string) => {
    setSearchTerm(value || "");
  };

  const toggleMap = (open: boolean) => {
    setShowMap(open);
  };

  const updateCurrentLocation = () => {
    // Show the map when location services are activated
    setShowMap(true);
    setLocationUnavailable(false);

    getCurrentPosition().then(
      (location: GeoLocation) => {
        const pos = {
          lat: location.latitude,
          lng: location.longitude
        };

        try {
          updateCurrentPositionPin(mapId, pos);
        } catch (error) {
          console.error("Could not show the current location on the map:", error);
        }
      },
      // Denied, unavailable, timed out or not supported: getCurrentPosition logs which
      () => setLocationUnavailable(true)
    );
  };

  return (
    <div
      className={classNames("flex flex-col gap-md", " @container/clinic-finder", {
        "overflow-y-hidden": showMap
      })}
    >
      <div className="flex flex-col gap-md">
        <SearchInput
          name="department-search"
          onSubmit={handleSearch}
          onInput={handleInput}
          placeholder={translationContext?.translations?.searchClinicPlaceholder}
        />
        <div className="flex flex-col md:flex-row gap-xs">
          <Button
            size={ButtonSize.SM}
            sizeSM={ButtonSize.SM}
            sizeMD={ButtonSize.SM}
            sizeLG={ButtonSize.SM}
            sizeXL={ButtonSize.SM}
            icon="/dist/sprite.svg#sys-location"
            color={ButtonColor.Green}
            additionalClass="[&_svg]:fill-dark-blue"
            onClick={() => updateCurrentLocation()}
            aria-label={translationContext?.translations?.activateLocationService}
            title={translationContext?.translations?.activateLocationService}
            data-online-booking-action="activateLocationService"
          >
            {translationContext?.translations?.activateLocationService}
          </Button>

          <Button
            size={ButtonSize.SM}
            sizeSM={ButtonSize.SM}
            sizeMD={ButtonSize.SM}
            sizeLG={ButtonSize.SM}
            sizeXL={ButtonSize.SM}
            icon="/svg/system/language-map.svg"
            color={ButtonColor.DarkBlue}
            additionalClass="[&_svg]:fill-white"
            onClick={() => toggleMap(!showMap)}
            aria-label={
              !showMap
                ? translationContext?.translations?.displayMap
                : translationContext?.translations?.hideMap
            }
            title={
              !showMap
                ? translationContext?.translations?.displayMap
                : translationContext?.translations?.hideMap
            }
            data-online-booking-action="showHideMap"
          >
            {!showMap
              ? translationContext?.translations?.displayMap
              : translationContext?.translations?.hideMap}
          </Button>
        </div>
        {locationUnavailable && (
          <InlineError
            message={translations?.locationUnavailable ?? ""}
            className="mt-0"
          />
        )}
      </div>

      {/* A stable focus target for after "Try again" */}
      <div
        ref={resultsRef}
        tabIndex={-1}
        className="flex flex-col gap-md outline-none"
        data-online-booking-department-results
      >
        {departmentsStatus === "error" && (
          <ErrorMessage error={loadError} onRetry={retryDepartments} />
        )}

        {departmentsStatus === "loading" && (
          <LoadingStatus
            loading={true}
            message={translationContext?.translations?.loadingClinics}
            className="justify-center py-xl"
            style={{ minHeight: `${LOADING_CONFIG.DEPARTMENT_MIN_HEIGHT}px` }}
          >
            <Spinner visible={true} />
          </LoadingStatus>
        )}

        {departmentsStatus === "loaded" && (
          <ul
            className={classNames(
              "flex flex-col w-[100cqw] max-h-[27.375rem] overflow-y-auto",
              {
                hidden: showMap
              }
            )}
            data-online-booking-department-list
          >
            {filteredDepartments.map((department, index) => (
              <li key={department.id ?? `index-${index}`}>
                {department.isActive ? (
                  <SelectionItem
                    name={department.name ?? ""}
                    value={department.name ?? ""}
                    labelSubTitle={department.address_info?.city}
                    labelTitle={department.name ?? ""}
                    onChange={() => {
                      onSelectDepartment(department);
                    }}
                    checked={false}
                  />
                ) : (
                  <OldBookingListItem
                    department={department}
                    oldBookingUrl={getOldBookingUrl(
                      oldBookingDomain,
                      department.id,
                      language
                    )}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        <p
          className={classNames("text-t-sm text-t-neutral-700", {
            "py-md": !!listMessage
          })}
          role="status"
          data-online-booking-no-results
        >
          {listMessage}
        </p>
      </div>

      <div
        className={classNames("py-xs", {
          "flex-[1_1_50%]": showMap,
          "hidden h-0": !showMap
        })}
      >
        <DepartmentMap
          id={mapId}
          visible={showMap}
          departments={filteredDepartments}
          oldBookingDomain={oldBookingDomain}
          onSelectDepartment={onSelectDepartment}
          defaultLatitude={defaultLatitude}
          defaultLongitude={defaultLongitude}
          defaultZoom={defaultZoom}
          mapboxAccessToken={mapboxAccessToken}
        />
      </div>
    </div>
  );
};

export default ProvetDepartmentFinder;
